// Shared blood-relationship search for the API and the visible family network.
// This module is browser-safe and uses only the supplied parent topology.

function buildAncestorMap(topology, startID, maxDepth) {
    const map = new Map();
    map.set(startID, {
        path: [{ personID: startID, via: null }],
        depth: 0,
    });
    let currentLevel = [startID];

    for (let depth = 1; depth <= maxDepth && currentLevel.length > 0; depth += 1) {
        const nextLevel = [];
        for (const childID of currentLevel) {
            const childEntry = map.get(childID);
            for (const parentID of (topology.parentsByPerson.get(childID) || [])) {
                if (map.has(parentID)) continue;
                map.set(parentID, {
                    path: [...childEntry.path, { personID: parentID, via: 'parent' }],
                    depth,
                });
                nextLevel.push(parentID);
            }
        }
        currentLevel = nextLevel;
    }

    return map;
}

function findBloodRelationshipPaths(topology, startID, targetID, maxDepth, maxPaths = 5) {
    const mapA = buildAncestorMap(topology, startID, maxDepth);
    const mapB = buildAncestorMap(topology, targetID, maxDepth);
    const visitedNodes = new Set([...mapA.keys(), ...mapB.keys()]).size;
    const commonAncestors = [];

    for (const [ancestorID, entryA] of mapA) {
        const entryB = mapB.get(ancestorID);
        if (!entryB) continue;
        const depthSum = entryA.depth + entryB.depth;
        if (depthSum > maxDepth) continue;
        commonAncestors.push({
            ancestorID,
            depthA: entryA.depth,
            depthB: entryB.depth,
            depthSum,
            pathA: entryA.path,
            pathB: entryB.path,
        });
    }

    commonAncestors.sort((a, b) => (
        a.depthSum - b.depthSum || String(a.ancestorID).localeCompare(String(b.ancestorID))
    ));

    const seenDepthPairs = new Set();
    const deduplicated = [];
    for (const ancestor of commonAncestors) {
        const key = `${ancestor.depthA}_${ancestor.depthB}`;
        if (seenDepthPairs.has(key)) continue;
        seenDepthPairs.add(key);
        deduplicated.push(ancestor);
    }

    const trueMrcas = deduplicated.filter(ancestor => {
        for (let index = 1; index < ancestor.pathA.length - 1; index += 1) {
            if (mapB.has(ancestor.pathA[index].personID)) return false;
        }
        for (let index = 1; index < ancestor.pathB.length - 1; index += 1) {
            if (mapA.has(ancestor.pathB[index].personID)) return false;
        }
        return true;
    });

    const paths = trueMrcas.slice(0, maxPaths).map(ancestor => {
        const descendingPath = [...ancestor.pathB]
            .reverse()
            .slice(1)
            .map(node => ({ personID: node.personID, via: 'child' }));
        return [...ancestor.pathA, ...descendingPath];
    });

    return { paths, visitedNodes };
}

module.exports = { buildAncestorMap, findBloodRelationshipPaths };
